import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { TrendingUp, Target, Code, Zap, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import PaywallPopup from './PaywallPopup';

const Guides = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axiosInstance.get('/user/subscription-status');
      // Guides are unlocked only with active subscription (monthly or lifetime)
      setIsUnlocked(response.data.has_subscription);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setIsUnlocked(false);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleGuideClick = (e) => {
    if (!isUnlocked) {
      e.preventDefault();
      setShowPaywall(true);
    }
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-12 h-12 animate-spin text-[#A78BFA]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guides-section">
      <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)]">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            Meta Kampagnen Leitfäden & Erklärungen
            {!isUnlocked && <Lock className="w-6 h-6 text-[#A78BFA] ml-2" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[rgba(245,245,247,0.7)] mb-4">
            Hier finden Sie umfassende Leitfäden und Erklärungen für erfolgreiche Meta-Werbekampagnen im Finanzsektor.
          </p>
          {!isUnlocked && (
            <div className="bg-[rgba(167,139,250,0.1)] border border-[rgba(167,139,250,0.3)] rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Lock className="w-6 h-6 text-[#A78BFA] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="font-bold text-purple-900 mb-1">Exklusiver Premium-Content</h4>
                  <p className="text-sm text-purple-800 mb-3">
                    Diese ausführlichen Leitfäden sind nur für Premium-Mitglieder verfügbar. Abonnieren Sie jetzt für vollen Zugang!
                  </p>
                  <Button
                    onClick={() => setShowPaywall(true)}
                    className="font-semibold"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    Jetzt Premium werden
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meta Pixel Integration */}
      <Card 
        className={`shadow-lg border-2 border-blue-100 relative ${!isUnlocked ? 'opacity-60 pointer-events-none' : ''}`} 
        data-testid="meta-pixel-guide"
      >
        {!isUnlocked && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center z-10 cursor-not-allowed">
            <div className="text-center p-6 bg-[rgba(255,255,255,0.05)] rounded-lg shadow-lg">
              <Lock className="w-12 h-12 text-[#A78BFA] mx-auto mb-2" />
              <p className="text-[rgba(245,245,247,0.9)] font-semibold">Premium Content</p>
            </div>
          </div>
        )}
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center">
              <Code className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle className="text-xl flex items-center gap-2">
              Meta Pixel Integration
              {!isUnlocked && <Lock className="w-5 h-5 text-[rgba(245,245,247,0.5)]" />}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full" disabled={!isUnlocked}>
            <AccordionItem value="what-is-pixel" data-testid="pixel-what" onClick={handleGuideClick}>
              <AccordionTrigger>Was ist der Meta Pixel?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 text-[rgba(245,245,247,0.9)]">
                  <p>
                    Der Meta Pixel ist ein Tracking-Code, den Sie auf Ihrer Website einbinden.
                    Er erfasst Besucheraktionen und ermöglicht:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Conversion-Tracking (z.B. Formularausfüllungen, Downloads)</li>
                    <li>Remarketing an Website-Besucher</li>
                    <li>Optimierung der Anzeigenauslieferung</li>
                    <li>Erstellung von Lookalike Audiences</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pixel-setup" data-testid="pixel-setup">
              <AccordionTrigger>Schritt-für-Schritt: Pixel einrichten</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 text-[rgba(245,245,247,0.9)]">
                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">1. Pixel erstellen</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Öffnen Sie den Meta Events Manager</li>
                      <li>Klicken Sie auf "Datenquellen verbinden" → "Web"</li>
                      <li>Wählen Sie "Meta Pixel" → "Verbinden"</li>
                      <li>Geben Sie einen Namen ein (z.B. "Ihre-Firma Pixel")</li>
                      <li>Geben Sie Ihre Website-URL ein</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">2. Pixel-Code kopieren</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Klicken Sie auf "Pixel-Code installieren"</li>
                      <li>Wählen Sie "Code manuell installieren"</li>
                      <li>Kopieren Sie den Pixel-Base-Code</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">3. Code auf Website einfügen</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Öffnen Sie Ihre Website im Editor</li>
                      <li>Fügen Sie den Code im {'<head>'}-Bereich aller Seiten ein</li>
                      <li>Speichern und Website veröffentlichen</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">4. Events einrichten</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Zurück im Events Manager → "Events hinzufügen"</li>
                      <li>Wählen Sie Events wie "Lead", "CompleteRegistration"</li>
                      <li>Definieren Sie die URL oder Button-Klicks für das Event</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg border border-[rgba(0,242,255,0.3)]">
                    <h4 className="font-bold text-green-800 mb-2">5. Testen</h4>
                    <p className="text-sm">
                      Nutzen Sie die "Meta Pixel Helper" Chrome-Extension, um zu prüfen,
                      ob der Pixel korrekt feuert.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Conversion API */}
      <Card 
        className={`shadow-lg border-2 border-[rgba(0,242,255,0.2)] relative ${!isUnlocked ? 'opacity-60 pointer-events-none' : ''}`} 
        data-testid="conversion-api-guide"
      >
        {!isUnlocked && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center z-10 cursor-not-allowed">
            <div className="text-center p-6 bg-[rgba(255,255,255,0.05)] rounded-lg shadow-lg">
              <Lock className="w-12 h-12 text-[#A78BFA] mx-auto mb-2" />
              <p className="text-[rgba(245,245,247,0.9)] font-semibold">Premium Content</p>
            </div>
          </div>
        )}
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#00F2FF]" />
            </div>
            <CardTitle className="text-xl flex items-center gap-2">
              Conversion API Setup
              {!isUnlocked && <Lock className="w-5 h-5 text-[rgba(245,245,247,0.5)]" />}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full" disabled={!isUnlocked}>
            <AccordionItem value="what-is-capi" data-testid="capi-what" onClick={handleGuideClick}>
              <AccordionTrigger>Was ist die Conversion API?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 text-[rgba(245,245,247,0.9)]">
                  <p>
                    Die Conversion API (CAPI) ist eine Server-zu-Server-Verbindung zwischen Ihrer Website
                    und Meta. Sie ergänzt den Meta Pixel und bietet:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Zuverlässigeres Tracking (unabhängig von Browser-Einstellungen)</li>
                    <li>Bessere Datenverfügbarkeit durch iOS 14+ Updates</li>
                    <li>Höhere Datenqualität und Conversion-Attribution</li>
                    <li>DSGVO-konforme Datenübermittlung</li>
                  </ul>
                  <p className="font-semibold text-blue-700 mt-3">
                    💡 Empfehlung: Nutzen Sie Pixel + CAPI zusammen für optimale Ergebnisse!
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="capi-setup" data-testid="capi-setup">
              <AccordionTrigger>Schritt-für-Schritt: CAPI einrichten</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 text-[rgba(245,245,247,0.9)]">
                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">1. Access Token generieren</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Events Manager öffnen → Ihr Pixel auswählen</li>
                      <li>"Einstellungen" → "Conversions API"</li>
                      <li>"Access Token generieren" klicken</li>
                      <li>Token sicher speichern (zeigt nur einmal!)</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">2. Server-Integration</h4>
                    <p className="text-sm mb-2">
                      Ihre IT oder Agentur benötigt:
                    </p>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li><strong>Pixel ID</strong>: Finden Sie im Events Manager</li>
                      <li><strong>Access Token</strong>: Der generierte Token</li>
                      <li><strong>API Endpoint</strong>: https://graph.facebook.com/v18.0/events</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">3. Events senden</h4>
                    <p className="text-sm mb-2">
                      Bei wichtigen Aktionen (z.B. Lead-Formular abgeschickt) sendet Ihr Server
                      die Daten an Meta:
                    </p>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Event-Name (z.B. "Lead")</li>
                      <li>Event-Zeit (Timestamp)</li>
                      <li>User-Daten (gehashed: E-Mail, Telefon)</li>
                      <li>Event-Quelle-URL</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">4. Event-Matching verbessern</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Verwenden Sie die gleiche Event-ID für Pixel & CAPI</li>
                      <li>Senden Sie so viele User-Parameter wie möglich</li>
                      <li>Hashen Sie personenbezogene Daten (SHA256)</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-bold text-yellow-800 mb-2">⚠️ Wichtig für Finanzdienstleister</h4>
                    <p className="text-sm">
                      Achten Sie auf DSGVO-Compliance. Holen Sie Einwilligungen ein
                      und hashen Sie personenbezogene Daten vor dem Versand.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Campaign Setup */}
      <Card 
        className={`shadow-lg border-2 border-[rgba(167,139,250,0.2)] relative ${!isUnlocked ? 'opacity-60 pointer-events-none' : ''}`} 
        data-testid="campaign-setup-guide"
      >
        {!isUnlocked && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center z-10 cursor-not-allowed">
            <div className="text-center p-6 bg-[rgba(255,255,255,0.05)] rounded-lg shadow-lg">
              <Lock className="w-12 h-12 text-[#A78BFA] mx-auto mb-2" />
              <p className="text-[rgba(245,245,247,0.9)] font-semibold">Premium Content</p>
            </div>
          </div>
        )}
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-[#A78BFA]" />
            </div>
            <CardTitle className="text-xl flex items-center gap-2">
              Kampagnen-Setup für Finanzdienstleister
              {!isUnlocked && <Lock className="w-5 h-5 text-[rgba(245,245,247,0.5)]" />}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full" disabled={!isUnlocked}>
            <AccordionItem value="campaign-structure" data-testid="campaign-structure" onClick={handleGuideClick}>
              <AccordionTrigger>Kampagnenstruktur verstehen</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 text-[rgba(245,245,247,0.9)]">
                  <p>Meta Ads haben eine 3-stufige Struktur:</p>
                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg space-y-3">
                    <div>
                      <h4 className="font-bold">1. Kampagne (Campaign)</h4>
                      <p className="text-sm">Definiert das Ziel (z.B. Leads, Traffic, Conversions)</p>
                    </div>
                    <div>
                      <h4 className="font-bold">2. Anzeigengruppe (Ad Set)</h4>
                      <p className="text-sm">Definiert Zielgruppe, Budget, Platzierung</p>
                    </div>
                    <div>
                      <h4 className="font-bold">3. Anzeige (Ad)</h4>
                      <p className="text-sm">Das kreative Element (Bild + Text)</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="campaign-create" data-testid="campaign-create">
              <AccordionTrigger>Schritt-für-Schritt: Kampagne erstellen</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 text-[rgba(245,245,247,0.9)]">
                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">Schritt 1: Kampagnenziel wählen</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Werbeanzeigenmanager öffnen → "Erstellen"</li>
                      <li><strong>Für Lead-Sammlung</strong>: Wählen Sie "Leads"</li>
                      <li><strong>Für Website-Traffic</strong>: Wählen Sie "Traffic" oder "Conversions"</li>
                      <li>Kampagnennamen vergeben (z.B. "Altersvorsorge_Q1_2025")</li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">Schritt 2: Anzeigengruppe konfigurieren</h4>
                    <ul className="list-disc ml-6 space-y-2 text-sm">
                      <li>
                        <strong>Conversion-Standort</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Lead Ad: "Instant Forms" wählen</li>
                          <li>Landing Page: "Website" wählen</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Budget & Zeitplan</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Tagesbudget: Min. 20-50€/Tag für Start</li>
                          <li>Laufzeit: Mindestens 7 Tage für Optimierung</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Zielgruppe</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Standort: Deutschland (oder spezifische Regionen)</li>
                          <li>Alter: 30-65 Jahre (je nach Produkt)</li>
                          <li>Detailliertes Targeting: "Finanzdienstleistungen", "Investitionen", "Altersvorsorge"</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Platzierungen</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Empfehlung für Start: "Automatische Platzierungen"</li>
                          <li>Später optimieren: Feed, Stories deaktivieren falls nötig</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg">
                    <h4 className="font-bold mb-2">Schritt 3: Anzeige erstellen</h4>
                    <ul className="list-disc ml-6 space-y-2 text-sm">
                      <li>
                        <strong>Format wählen</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Einzelbild: Am einfachsten, hohe Conversion</li>
                          <li>Karussell: Mehrere Vorteile zeigen</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Bild hochladen</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Nutzen Sie die hier generierten Bilder!</li>
                          <li>Format: 1080x1080px (quadratisch) ideal</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Text hinzufügen</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Primärtext: Nutzen Sie die generierte AdCopy!</li>
                          <li>Überschrift: Kurz & prägnant (max. 40 Zeichen)</li>
                          <li>Call-to-Action: "Mehr erfahren", "Jetzt anfragen"</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Lead-Formular / Ziel-URL</strong>:
                        <ul className="list-circle ml-4 mt-1">
                          <li>Lead Ad: Formular mit Name, E-Mail, Telefon</li>
                          <li>Landing Page: Ihre Website-URL mit Tracking-Parameter</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg border border-[rgba(0,242,255,0.3)]">
                    <h4 className="font-bold text-green-800 mb-2">Schritt 4: Veröffentlichen & Überwachen</h4>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Klicken Sie auf "Veröffentlichen"</li>
                      <li>Meta prüft die Anzeige (dauert 24-48h)</li>
                      <li>Überwachen Sie täglich: CPL (Cost per Lead), CTR, Relevanz-Score</li>
                      <li>Nach 3-7 Tagen: A/B-Tests mit verschiedenen Bildern/Texten</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="lead-vs-landing" data-testid="lead-vs-landing">
              <AccordionTrigger>Lead Ad vs. Landing Page: Wann was nutzen?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 text-[rgba(245,245,247,0.9)]">
                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg border border-[rgba(0,242,255,0.3)]">
                    <h4 className="font-bold text-green-800 mb-2">✓ Lead Ad - Für Quantität</h4>
                    <p className="text-sm mb-2">Nutzen Sie Lead Ads wenn:</p>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Sie schnell viele Kontaktdaten sammeln möchten</li>
                      <li>Ihr Angebot einfach zu verstehen ist</li>
                      <li>Sie Erstgespräche, Newsletter-Anmeldungen anbieten</li>
                      <li>Ihr Budget begrenzt ist (niedrigerer CPL)</li>
                    </ul>
                    <p className="text-sm mt-2 font-semibold">
                      💡 Beispiele: Kostenlose Erstberatung, E-Book Download, Newsletter
                    </p>
                  </div>

                  <div className="bg-[rgba(0,242,255,0.1)] p-4 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">✓ Landing Page - Für Qualität</h4>
                    <p className="text-sm mb-2">Nutzen Sie Landing Pages wenn:</p>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li>Ihr Produkt erklärungsbedürftig ist</li>
                      <li>Sie qualifizierte, kaufbereite Leads wollen</li>
                      <li>Sie hochwertige Dienstleistungen anbieten (>5.000€)</li>
                      <li>Sie mehr Informationen vom Interessenten benötigen</li>
                    </ul>
                    <p className="text-sm mt-2 font-semibold">
                      💡 Beispiele: Vermögensberatung, Baufinanzierung, Unternehmenskredite
                    </p>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-bold text-yellow-800 mb-2">💡 Pro-Tipp: Kombistrategie</h4>
                    <p className="text-sm">
                      Starten Sie mit Lead Ads für schnelle Datensammlung. Nutzen Sie dann
                      Remarketing auf Landing Page für qualifizierte Leads.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="optimization-tips" data-testid="optimization-tips">
              <AccordionTrigger>Optimierungs-Tipps für Finanzdienstleister</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 text-[rgba(245,245,247,0.9)]">
                  <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg">
                    <h4 className="font-bold mb-1 text-sm">✓ Vertrauenswürdigkeit betonen</h4>
                    <p className="text-sm">Zeigen Sie Zertifikate, Auszeichnungen, Kundenbewertungen</p>
                  </div>
                  <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg">
                    <h4 className="font-bold mb-1 text-sm">✓ Klarer Nutzen</h4>
                    <p className="text-sm">Vermeiden Sie Fachjargon. Sprechen Sie Vorteile an: "Mehr Rendite", "Sichere Zukunft"</p>
                  </div>
                  <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg">
                    <h4 className="font-bold mb-1 text-sm">✓ Mobile-First</h4>
                    <p className="text-sm">80% der Nutzer sehen Ihre Anzeige auf dem Handy. Testen Sie mobil!</p>
                  </div>
                  <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg">
                    <h4 className="font-bold mb-1 text-sm">✓ A/B-Testing</h4>
                    <p className="text-sm">Testen Sie 2-3 Bilder und Texte gleichzeitig. Pausieren Sie schlechte Performer.</p>
                  </div>
                  <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg">
                    <h4 className="font-bold mb-1 text-sm">✓ Follow-Up System</h4>
                    <p className="text-sm">Kontaktieren Sie Leads innerhalb 24h. Schnelle Reaktion = höhere Conversion.</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Paywall Popup */}
      <PaywallPopup
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type="subscription"
      />
    </div>
  );
};

export default Guides;
